import React from "react";
import {Button, Space} from "antd";
import {EyeOutlined} from "@ant-design/icons";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {ResponsiveDataTable} from "components/_defaultComponents/ResponsiveDataTable";
import {TICKET_ONBOARDING_CATEGORY_TYPES} from "constants/ticket";
import {Shortener} from "components/_defaultComponents/Shortener/Shortener";
import {TicketStatusTag} from "./TicketStatusTag";
import {TicketPriorityTag} from "./TicketPriorityTag";
import {TicketElapsedTime} from "./TicketElapsedTime";
import {
    formatDate,
    formatDateTime,
    formatTime,
    getActorLabel,
    getAssigneeLabel,
    getManagerCompanyLabel,
    getOwnerRoute
} from "./ticketUtils";

export const TicketsTable = ({
    category,
    rows,
    total,
    loading,
    pagination,
    paginated = true,
    compact = false,
    ownerView = false,
    roleLabels,
    onView,
    onPageChange
}) => {
    const {t} = useTranslation(["tickets"]);
    const label = key => t(`list.columns.${key}`, {ns: "tickets"});
    const cell = key => () => ({"data-label": label(key)});

    const ownerLink = (record, content) => {
        const route = getOwnerRoute(record);
        return route ? <Link to={route}>{content}</Link> : content;
    };

    const dateColumn = {
        title: label("date"),
        key: "date",
        render: (_, record) => (
            <>
                <div>{formatDate(record.createdAt)}</div>
                <div>{formatTime(record.createdAt)}</div>
            </>
        ),
        onCell: cell("date")
    };

    const isOnboardingCategory = Boolean(TICKET_ONBOARDING_CATEGORY_TYPES[category]);

    const ownerNameColumn = {
        title: label("ownerName"),
        key: "ownerName",
        render: (_, record) => {
            const name = record.owner?.name || "-";
            return isOnboardingCategory ? ownerLink(record, name) : name;
        },
        onCell: cell("ownerName")
    };

    const categoryColumns = {
        courier: [
            {
                title: label("courierTckn"),
                key: "courierTckn",
                render: (_, record) => record.owner?.tckn || "-",
                onCell: cell("courierTckn")
            },
            {
                title: label("courierName"),
                key: "courierName",
                render: (_, record) => ownerLink(record, record.owner?.name || "-"),
                onCell: cell("courierName")
            },
            {
                title: label("managerCompany"),
                key: "managerCompany",
                render: (_, record) => getManagerCompanyLabel(record.ownerDetail?.managerCompany),
                onCell: cell("managerCompany")
            }
        ],
        company: [
            {
                title: label("branch"),
                key: "branch",
                render: (_, record) =>
                    ownerLink(
                        record,
                        <>
                            <div>{record.ownerDetail?.companyName || "-"}</div>
                            <div>{record.owner?.name || "-"}</div>
                        </>
                    ),
                onCell: cell("branch")
            },
            {
                title: label("branchCode"),
                key: "branchCode",
                render: (_, record) => record.ownerDetail?.subCompanyCode || "-",
                onCell: cell("branchCode")
            },
            {
                title: label("managerCompany"),
                key: "managerCompany",
                render: (_, record) => getManagerCompanyLabel(record.ownerDetail?.managerCompany),
                onCell: cell("managerCompany")
            }
        ],
        onboarding_company: [],
        onboarding_agency: [],
        other: []
    };

    const columns = [
        {
            title: label("number"),
            dataIndex: "number",
            key: "number",
            render: number => <strong>{number}</strong>,
            onCell: cell("number")
        },
        dateColumn,
        ownerNameColumn,
        {
            title: label("createdBy"),
            key: "createdBy",
            render: (_, record) => getActorLabel(record.createdBy, roleLabels),
            onCell: cell("createdBy")
        },
        {
            title: label("assignee"),
            key: "assignee",
            render: (_, record) => (
                <div style={{maxWidth: 240}}>
                    <Shortener text={getAssigneeLabel(record, roleLabels)} length={64} />
                </div>
            ),
            onCell: cell("assignee")
        },
        ...(categoryColumns[category] || []),
        {
            title: label("title"),
            key: "title",
            render: (_, record) => record.ticketType?.name || "-",
            onCell: cell("title")
        },
        {
            title: label("lastAction"),
            key: "lastAction",
            render: (_, record) => formatDateTime(record.updatedAt || record.createdAt),
            onCell: cell("lastAction")
        },
        {
            title: label("priority"),
            dataIndex: "priority",
            key: "priority",
            render: priority => <TicketPriorityTag priority={priority} />,
            onCell: cell("priority")
        },
        {
            title: label("status"),
            dataIndex: "status",
            key: "status",
            render: status => <TicketStatusTag status={status} />,
            onCell: cell("status")
        },
        {
            title: label("elapsed"),
            key: "elapsed",
            render: (_, record) => <TicketElapsedTime ticket={record} />,
            onCell: cell("elapsed")
        },
        {
            title: label("action"),
            key: "action",
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => onView(record)}
                    >
                        {t("list.view", {ns: "tickets"})}
                    </Button>
                </Space>
            ),
            onCell: cell("action")
        }
    ];

    // Başlık, talep yönetimi sayfasında ilgili talebin detayını açar
    const columnByKey = Object.fromEntries(columns.map(column => [column.key, column]));
    const linkedTitleColumn = {
        ...columnByKey.title,
        render: (_, record) => (
            <Link to={`/ticket-management?ticketId=${record._id}`}>
                {record.ticketType?.name || "-"}
            </Link>
        )
    };
    const pickColumns = keys =>
        keys.map(key => (key === "title" ? linkedTitleColumn : columnByKey[key]));

    // Başvuru detayındaki kısa liste
    const compactColumns = pickColumns(["date", "number", "title", "assignee", "status", "action"]);
    // Kurye / Alt Firma detayındaki talepler sekmesi
    const ownerColumns = pickColumns([
        "number",
        "date",
        "title",
        "lastAction",
        "status",
        "elapsed",
        "action"
    ]);

    // Formdan gelen talepte açan kişi yoktur; talep sahibi Ad-Soyad olarak gösterilir
    const fullNameColumn = {
        title: label("fullName"),
        key: "fullName",
        render: (_, record) => record.owner?.name || "-",
        onCell: cell("fullName")
    };
    const otherColumns = [
        columnByKey.number,
        columnByKey.date,
        columnByKey.assignee,
        columnByKey.title,
        fullNameColumn,
        columnByKey.lastAction,
        columnByKey.priority,
        columnByKey.status,
        columnByKey.elapsed,
        columnByKey.action
    ];

    let tableColumns = columns;
    if (compact) {
        tableColumns = compactColumns;
    } else if (ownerView) {
        tableColumns = ownerColumns;
    } else if (category === "other") {
        tableColumns = otherColumns;
    }

    return (
        <ResponsiveDataTable
            size="small"
            columns={tableColumns}
            dataSource={rows}
            rowKey="_id"
            loading={loading}
            scroll={{x: "max-content"}}
            pagination={
                paginated && {
                    hideOnSinglePage: true,
                    current: Math.floor(pagination.skip / pagination.limit) + 1,
                    pageSize: pagination.limit,
                    total,
                    showTotal: count => t("list.total", {ns: "tickets", total: count}),
                    onChange: onPageChange
                }
            }
        />
    );
};
